import { warehouseClient, extractStatusCode, extractResponseMessage } from "@/infrastructure/http/warehouseClient";
import { PlanLimitError } from "@/infrastructure/errors/planLimitError";
import type {
  WarehouseRecord,
  CreateWarehouseInput,
  UpdateWarehouseInput,
  UpdateWarehouseStatusInput,
  WarehouseErrorCode,
  ApiEnvelope,
} from "@/domain/types";

export function createWarehouseError(code: WarehouseErrorCode): Error {
  const err = new Error(code);
  err.name = "WarehouseError";
  return err;
}

export function isWarehouseError(err: unknown): err is Error & { message: WarehouseErrorCode } {
  return err instanceof Error && err.name === "WarehouseError";
}

function mapError(statusCode: number): WarehouseErrorCode {
  if (statusCode === 409) return "DUPLICATE_CODE";
  if (statusCode === 400) return "VALIDATION";
  if (statusCode === 404) return "NOT_FOUND";
  if (statusCode === 403) return "FORBIDDEN";
  if (statusCode === 401) return "UNAUTHORIZED";
  return "UNKNOWN";
}

export async function listWarehouses(): Promise<WarehouseRecord[]> {
  const response = await warehouseClient.get<ApiEnvelope<unknown[]>>("/warehouse");
  const raw = response.data.data ?? [];
  return raw.map(normalizeWarehouse);
}

function normalizeWarehouse(raw: unknown): WarehouseRecord {
  const r = raw as Record<string, unknown>;
  return {
    warehouse_id: (r.warehouse_id ?? r.id ?? "") as string,
    warehouse_tenant_id: (r.warehouse_tenant_id ?? r.tenantId ?? "") as string,
    warehouse_name: (r.warehouse_name ?? r.name ?? "") as string,
    warehouse_code: (r.warehouse_code ?? r.code ?? "") as string,
    warehouse_description: (r.warehouse_description ?? r.description ?? "") as string,
    warehouse_state: (r.warehouse_state ?? r.status ?? r.state ?? "ACTIVE") as WarehouseState,
    warehouse_created_at: (r.warehouse_created_at ?? r.createAt ?? r.createdAt ?? "") as string,
    type_use: normalizeTypeUse(r),
  };
}

function normalizeTypeUse(r: Record<string, unknown>): { id: string; name: string } {
  if (r.type_use && typeof r.type_use === "object") return r.type_use as { id: string; name: string };
  if (r.typeUseDetails && typeof r.typeUseDetails === "object") return r.typeUseDetails as { id: string; name: string };
  const typeUseId = (r.typeUse ?? "") as string;
  return { id: typeUseId, name: "" };
}

export async function createWarehouse(input: CreateWarehouseInput): Promise<void> {
  try {
    await warehouseClient.post("/warehouse", input);
  } catch (err) {
    const status = extractStatusCode(err);
    if (status === 403) {
      const msg = extractResponseMessage(err);
      if (msg) throw new PlanLimitError(msg);
    }
    throw createWarehouseError(mapError(status));
  }
}

export async function updateWarehouse(input: UpdateWarehouseInput): Promise<void> {
  try {
    await warehouseClient.patch("/warehouse", input);
  } catch (err) {
    throw createWarehouseError(mapError(extractStatusCode(err)));
  }
}

export async function updateWarehouseStatus(input: UpdateWarehouseStatusInput): Promise<void> {
  try {
    await warehouseClient.patch("/warehouse/status", input);
  } catch (err) {
    throw createWarehouseError(mapError(extractStatusCode(err)));
  }
}
